import { ExecutorEntity, Pipeline, PipelineEnvironment, PipelineRepository, PipelineStep } from '@telespot/domain';
import { PipelineStepOrderService, PipelineStepParamsBuilder, PipelineIntegrityService } from '@protocols/domain';

import { PipelineStepDraftDto } from '../../dtos/pipeline-step-draft.dto';

export interface SavePipelineInput {
  id?: string;
  name: { [lang: string]: string };
  description: { [lang: string]: string };
  env: PipelineEnvironment;
  steps: PipelineStepDraftDto[];
  deletedStepIds: string[];
}

export interface SavePipelineOutput {
  pipelineId: string;
}

export enum SavePipelineErrorCode {
  MISSING_NAME_EN = 'A non-empty `name.en` is required to save a pipeline.',
  MISSING_PERSISTED_ID = 'PipelineRepository.savePipeline() returned an empty id.',
}

export class SavePipelineException extends Error {
  constructor(public readonly code: SavePipelineErrorCode) {
    super(code.toString());
    this.name = 'SavePipelineException';
    Object.setPrototypeOf(this, SavePipelineException.prototype);
  }
}

export interface ISavePipelineCommand {
  execute(input: SavePipelineInput): Promise<SavePipelineOutput>;
}

export class SavePipelineCommand implements ISavePipelineCommand {
  constructor(private readonly pipelineRepository: PipelineRepository) {}

  private withSearchTerm(name: { [lang: string]: string }): { [lang: string]: string } {
    const languages: { [lang: string]: string } = {};
    for (const [key, value] of Object.entries(name)) {
      if (key !== 'searchTerm') languages[key] = value;
    }
    const searchTerm = Object.values(languages)
      .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
      .join(',');
    return { ...languages, searchTerm };
  }

  private toPipelineStep(draft: PipelineStepDraftDto, pipelineId: string, env: PipelineEnvironment): PipelineStep {
    const params =
      draft.mode === 'manual'
        ? draft.params
        : PipelineStepParamsBuilder.build({
            env,
            task: draft.task,
            categories: draft.categories,
            multiple: draft.multiple,
            allowNewOptions: draft.allowNewOptions,
            enableMosaicView: draft.enableMosaicView,
            counter: draft.counter,
          });
    return new PipelineStep({
      id: draft.id,
      pipelineId,
      order: draft.order,
      task: draft.task,
      executorEntity: draft.executorEntity,
      executorId: draft.executorEntity === ExecutorEntity.ALGORITHM ? draft.executorId : undefined,
      trigger: draft.trigger,
      generateFinding: draft.generateFinding,
      params,
    });
  }

  public async execute(input: SavePipelineInput): Promise<SavePipelineOutput> {
    if (!PipelineIntegrityService.isDisplayNameValid(input.name)) {
      throw new SavePipelineException(SavePipelineErrorCode.MISSING_NAME_EN);
    }

    const pipeline = new Pipeline({
      id: input.id,
      name: this.withSearchTerm(input.name),
      description: input.description ?? {},
      env: input.env,
    });

    const pipelineId = await this.pipelineRepository.savePipeline(pipeline);
    if (!pipelineId) {
      throw new SavePipelineException(SavePipelineErrorCode.MISSING_PERSISTED_ID);
    }

    const normalizedOrders = PipelineStepOrderService.normalize(input.steps.map((s) => s.order));
    const normalizedSteps = input.steps.map((step, i) => ({
      ...step,
      order: normalizedOrders[i],
    }));
    for (const draft of normalizedSteps) {
      await this.pipelineRepository.saveStep(this.toPipelineStep(draft, pipelineId, input.env));
    }

    for (const stepId of input.deletedStepIds) {
      await this.pipelineRepository.delete(stepId);
    }

    return { pipelineId };
  }
}
